import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { InstallButton } from "@/components/InstallButton";
import { useTranslation } from "@/i18n";
import { setLang } from "@/i18n";
import {
  SquaresFour, BookBookmark, Robot, Trophy, FileText, CrownSimple,
  SignOut, ShieldStar, List, X, Users, ChalkboardTeacher, Stack, FileMagnifyingGlass, Rocket, Buildings, Compass, Timer, ArrowSquareOut, Cards, CloudSlash, CloudCheck, ArrowsClockwise,
} from "@phosphor-icons/react";
import { useState, useEffect } from "react";
import { pendingCount, flushQueue, isOnline } from "@/lib/offlineSync";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV_STUDENT = [
  { to: "/app", label: "Tableau de bord", icon: SquaresFour, exact: true, tid: "nav-dashboard" },
  { to: "/app/diagnostic", label: "Plan de réussite", icon: Compass, tid: "nav-diagnostic" },
  { to: "/app/cours", label: "Cours", icon: BookBookmark, tid: "nav-courses" },
  { to: "/app/filieres", label: "Filières techniques", icon: Stack, tid: "nav-filieres" },
  { to: "/app/sprint", label: "Dernière ligne droite", icon: Rocket, tid: "nav-sprint" },
  { to: "/app/tuteur", label: "Professeur IA", icon: Robot, tid: "nav-tutor" },
  { to: "/app/simulateur", label: "Examens blancs", icon: Timer, tid: "nav-simulator" },
  { to: "/app/revisions", label: "Révisions", icon: Cards, tid: "nav-revisions" },
  { to: "/app/examens", label: "Banque de sujets", icon: FileText, tid: "nav-archive" },
  { to: "/app/classement", label: "Classement", icon: Trophy, tid: "nav-leaderboard" },
  { to: "/app/tarifs", label: "Abonnement", icon: CrownSimple, tid: "nav-pricing" },
];

const NAV_PARENT = [
  { to: "/app/parent", label: "Mon enfant", icon: Users, exact: true, tid: "nav-parent" },
  { to: "/app/tarifs", label: "Abonnement", icon: CrownSimple, tid: "nav-pricing" },
];

const NAV_TEACHER = [
  { to: "/app/enseignant", label: "Espace enseignant", icon: ChalkboardTeacher, exact: true, tid: "nav-teacher" },
  { to: "/app/tuteur", label: "Professeur IA", icon: Robot, tid: "nav-tutor" },
  { to: "/app/cours", label: "Cours", icon: BookBookmark, tid: "nav-courses" },
];

const NAV_SCHOOL = [
  { to: "/app/etablissement", label: "Établissement", icon: Buildings, exact: true, tid: "nav-school" },
  { to: "/app/cours", label: "Cours", icon: BookBookmark, tid: "nav-courses" },
  { to: "/app/tarifs", label: "Licences", icon: CrownSimple, tid: "nav-pricing" },
];

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [online, setOnline] = useState(isOnline());
  const [pending, setPending] = useState(pendingCount());
  const platformUrl = process.env.REACT_APP_PLATFORM_URL;
  const platformName = process.env.REACT_APP_PLATFORM_NAME || "la plateforme";

  useEffect(() => {
    const upOnline = () => { setOnline(true); flushQueue(); };
    const upOffline = () => setOnline(false);
    const upSync = () => setPending(pendingCount());
    window.addEventListener("online", upOnline);
    window.addEventListener("offline", upOffline);
    window.addEventListener("yaya-sync-change", upSync);
    return () => {
      window.removeEventListener("online", upOnline);
      window.removeEventListener("offline", upOffline);
      window.removeEventListener("yaya-sync-change", upSync);
    };
  }, []);

  const label = (item) => t(`nav.${item.tid.replace("nav-", "")}`, item.label);
  const toggleLang = () => setLang(i18n.language === "fr" ? "en" : "fr");

  const StatusBar = (
    <div className="flex items-center gap-2 px-2">
      <button data-testid="lang-toggle" onClick={toggleLang}
        className="rounded-lg border border-border px-2 py-1 text-xs font-bold text-ink/70 hover:bg-sand">
        {i18n.language === "fr" ? "FR" : "EN"} · {i18n.language === "fr" ? "EN" : "FR"}
      </button>
      {!online ? (
        <span data-testid="offline-indicator" className="inline-flex items-center gap-1 rounded-lg bg-gold/15 px-2 py-1 text-xs font-semibold text-[#8a6d1e]">
          <CloudSlash size={14} /> {t("common.offline")}
        </span>
      ) : pending > 0 ? (
        <button data-testid="sync-pending" onClick={() => flushQueue()} className="inline-flex items-center gap-1 rounded-lg bg-terracotta/10 px-2 py-1 text-xs font-semibold text-terracotta">
          <ArrowsClockwise size={14} /> {pending} {t("common.pending")}
        </button>
      ) : (
        <span data-testid="online-indicator" className="inline-flex items-center gap-1 rounded-lg bg-forest/10 px-2 py-1 text-xs font-semibold text-forest">
          <CloudCheck size={14} /> {t("common.online")}
        </span>
      )}
    </div>
  );

  const isActive = (item) =>
    item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);

  const baseNav = user?.role === "parent" ? NAV_PARENT : user?.role === "teacher" ? NAV_TEACHER : user?.role === "school" ? NAV_SCHOOL : NAV_STUDENT;
  const items = [...baseNav];
  if (user?.role === "admin") items.push({ to: "/app/admin", label: "Back-office", icon: ShieldStar, tid: "nav-admin" });

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5">
        <Link to="/app" onClick={() => setOpen(false)}><Logo /></Link>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.tid}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
                active ? "bg-terracotta text-white shadow-[0_6px_18px_rgba(255,75,10,0.3)]" : "text-ink/70 hover:bg-sand"
              }`}
            >
              <Icon size={20} weight={active ? "fill" : "regular"} />
              {label(item)}
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-border p-3">
        <InstallButton className="mb-2 flex w-full items-center justify-center gap-2 rounded-xl bg-terracotta px-3.5 py-2.5 text-sm font-bold text-white transition hover:bg-[#E03E00]" />
        <div className="mb-2">{StatusBar}</div>
        {platformUrl && (
          <a
            href={platformUrl}
            data-testid="platform-link"
            className="mb-2 flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-forest transition-colors hover:bg-forest/5"
          >
            <ArrowSquareOut size={20} weight="bold" />
            Retour à {platformName}
          </a>
        )}
        <div className="flex items-center gap-3 rounded-xl px-2 py-2">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-forest text-white text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-ink">{user?.name}</div>
            <div className="truncate text-xs text-muted-foreground capitalize">{user?.role}</div>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}
            className="rounded-lg p-2 text-muted-foreground hover:bg-sand hover:text-terracotta">
            <SignOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-border bg-white/70 backdrop-blur-xl lg:block">
        {SidebarInner}
      </aside>

      {/* Mobile top bar */}
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-border bg-cream/85 px-4 py-3 backdrop-blur-xl lg:hidden">
        <Logo size={32} />
        <div className="flex items-center gap-2">
          {StatusBar}
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="rounded-lg p-2 hover:bg-sand">
            <List size={24} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-cream shadow-2xl">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-4 rounded-lg p-2 hover:bg-sand">
              <X size={22} />
            </button>
            {SidebarInner}
          </div>
        </div>
      )}

      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-4 py-6 pb-24 sm:px-6 lg:px-10 lg:py-10 lg:pb-10">{children}</div>
      </main>

      {/* Mobile bottom navigation (Nomad-style) */}
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-white/95 backdrop-blur-xl lg:hidden" data-testid="mobile-bottom-nav">
        <div className="mx-auto flex max-w-lg items-stretch justify-around px-1">
          {items.slice(0, 4).map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link key={item.to} to={item.to} data-testid={`bottom-${item.tid}`}
                className={`flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold transition-colors ${active ? "text-terracotta" : "text-ink/50"}`}>
                <Icon size={22} weight={active ? "fill" : "regular"} />
                <span className="max-w-[68px] truncate">{label(item)}</span>
              </Link>
            );
          })}
          <button onClick={() => setOpen(true)} data-testid="bottom-menu-btn"
            className="flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold text-ink/50">
            <List size={22} />
            <span>Menu</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
